import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateCouponPayload,
  PromotionCampaignListItem,
  SalesApiService,
  UpdateCouponPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-coupon-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.coupons.form.createTitle') : t('sales.coupons.form.editTitle')" [description]="t('sales.coupons.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/coupons']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>
        <section class="card grid">
          <label><span>{{ t('sales.coupons.fields.code') }}</span><input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" /></label>
          <label>
            <span>{{ t('sales.coupons.fields.campaign') }}</span>
            <p-select [options]="campaignOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.campaignId" [showClear]="true" appendTo="body" />
          </label>
          <label>
            <span>{{ t('sales.coupons.fields.discountType') }}</span>
            <p-select [options]="discountTypes" optionLabel="label" optionValue="value" [(ngModel)]="form.discountType" appendTo="body" />
          </label>
          <label><span>{{ t('sales.coupons.fields.discountValue') }}</span><p-inputNumber [(ngModel)]="form.discountValue" [min]="0" /></label>
          <label><span>{{ t('sales.coupons.fields.minOrderTotal') }}</span><p-inputNumber [(ngModel)]="form.minOrderTotal" [min]="0" /></label>
          <label><span>{{ t('sales.coupons.fields.maxRedemptions') }}</span><p-inputNumber [(ngModel)]="form.maxRedemptions" [min]="0" /></label>
          <label><span>{{ t('sales.coupons.fields.validFrom') }}</span><ain-date-input [(ngModel)]="form.validFromUtc" /></label>
          <label><span>{{ t('sales.coupons.fields.validTo') }}</span><ain-date-input [(ngModel)]="form.validToUtc" /></label>
          @if (!isCreateMode()) {
            <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.coupons.active') }}</span></label>
          }
        </section>
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; margin-bottom: 10px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesCouponDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly campaigns = signal<PromotionCampaignListItem[]>([]);

  readonly discountTypes = [
    { value: 'Percent', label: 'Percent' },
    { value: 'FixedAmount', label: 'Fixed amount' }
  ];

  form = {
    code: '',
    campaignId: null as string | null,
    discountType: 'Percent',
    discountValue: 10,
    minOrderTotal: null as number | null,
    maxRedemptions: null as number | null,
    validFromUtc: null as string | null,
    validToUtc: null as string | null,
    isActive: true
  };

  ngOnInit(): void {
    this.api.listPromotionCampaigns().subscribe({
      next: rows => this.campaigns.set(rows.filter(item => item.isActive)),
      error: () => this.campaigns.set([])
    });

    const id = this.route.snapshot.paramMap.get('couponId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) {
      this.form.validFromUtc = new Date().toISOString();
      this.loading.set(false);
      return;
    }
    this.api.getCoupon(id!).subscribe({
      next: row => {
        this.form = {
          code: row.code,
          campaignId: row.campaignId ?? null,
          discountType: row.discountType,
          discountValue: row.discountValue,
          minOrderTotal: row.minOrderTotal ?? null,
          maxRedemptions: row.maxRedemptions ?? null,
          validFromUtc: row.validFromUtc,
          validToUtc: row.validToUtc ?? null,
          isActive: row.isActive
        };
        this.loading.set(false);
      },
      error: () => { this.errorMessage.set(this.t('sales.coupons.errorSave')); this.loading.set(false); }
    });
  }

  campaignOptions(): { label: string; value: string }[] {
    return this.campaigns().map(c => ({ label: `${c.code} - ${c.name}`, value: c.id }));
  }

  save(): void {
    this.saving.set(true);
    const validity = {
      validFromUtc: this.form.validFromUtc ?? undefined,
      validToUtc: this.form.validToUtc ?? undefined
    };
    if (this.isCreateMode()) {
      const payload: CreateCouponPayload = {
        code: this.form.code,
        campaignId: this.form.campaignId,
        discountType: this.form.discountType,
        discountValue: this.form.discountValue,
        minOrderTotal: this.form.minOrderTotal,
        maxRedemptions: this.form.maxRedemptions,
        ...validity
      };
      this.api.createCoupon(payload).subscribe({
        next: row => void this.router.navigate(['/sales/coupons', row.id]),
        error: () => this.failSave()
      });
      return;
    }
    const id = this.route.snapshot.paramMap.get('couponId')!;
    const payload: UpdateCouponPayload = {
      campaignId: this.form.campaignId,
      discountType: this.form.discountType,
      discountValue: this.form.discountValue,
      minOrderTotal: this.form.minOrderTotal,
      maxRedemptions: this.form.maxRedemptions,
      isActive: this.form.isActive,
      ...validity
    };
    this.api.updateCoupon(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => this.failSave()
    });
  }

  private failSave(): void { this.errorMessage.set(this.t('sales.coupons.errorSave')); this.saving.set(false); }
  t(key: string): string { return this.localization.translate(key); }
}
